import { X } from 'lucide-react';
import { createContext, ReactNode, useContext, useState } from 'react';
import { cn } from '@/lib/utils';
import { getApiNotice } from '../ApiNotice/apiNotices';

export type ToolPageVariant = 'default' | 'spotify';

/** Lets the empty results panel offer the example of the page that holds it. */
const ToolExampleContext = createContext<{ onExample?: () => void; label?: string; disabled?: boolean }>({});

export type ToolPageProps = {
  title: string;
  /** One short sentence: what this tool does. */
  description?: string;
  /** Quiet helper line (e.g. how to copy a playlist link). */
  hint?: string;
  onExample?: () => void;
  exampleLabel?: string;
  exampleDisabled?: boolean;
  /** Optional alert / notice under the title. */
  banner?: ReactNode;
  /** Spotify login required (spotify variant). */
  needsAuth?: boolean;
  onAuthClick?: () => void;
  /**
   * The tool can run read-only on the shared demo connection. Shown instead of
   * the connect prompt: the visitor is not blocked, so the status is not an error.
   */
  isDemo?: boolean;
  demoNotice?: string;
  variant?: ToolPageVariant;
  /** Width of the content under the header. The header always keeps the shared left edge. */
  maxWidth?: number | string;
  /** Limit children width further (forms). */
  contentMaxWidth?: number | string;
  /** Extra header actions (toggles, etc.) under the example link. */
  headerExtra?: ReactNode;
  children?: ReactNode;
};

const dismissKey = (noticeId: string) => `apiNoticeDismissed:${noticeId}`;

// Storage can throw in a private window. The notice then shows until the page reloads.
function readDismissed(noticeId: string): boolean {
  try {
    return localStorage.getItem(dismissKey(noticeId)) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(noticeId: string) {
  try {
    localStorage.setItem(dismissKey(noticeId), '1');
  } catch {}
}

const toCssWidth = (value: number | string | undefined) => (typeof value === 'number' ? `${value}px` : value);

/**
 * One status row with one action, so a page never stacks several messages about one fact.
 */
export function ToolStatus({
  children,
  actionLabel,
  onAction,
  tone = 'default',
  onDismiss,
  className,
}: {
  children: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  tone?: 'default' | 'spotify' | 'warning';
  onDismiss?: () => void;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn('flex min-h-11 items-center gap-2.5 rounded-xl bg-white/[0.06] px-3 py-2 text-sm leading-snug text-white/90', className)}
    >
      <span
        aria-hidden
        className={cn(
          'size-[7px] shrink-0 rounded-full',
          tone === 'spotify' && 'bg-[#1DB954]',
          tone === 'warning' && 'bg-amber-400',
          tone === 'default' && 'bg-primary'
        )}
      />
      {/* The action follows the text at compact width, so a long note does not wrap into a narrow column. */}
      <div className="min-w-0 flex-1 text-pretty lg:flex lg:items-center lg:gap-2.5">
        <span className="lg:min-w-0 lg:flex-1">{children}</span>
        {actionLabel && onAction ? (
          <>
            {' '}
            <button
              type="button"
              onClick={onAction}
              className={cn(
                'relative inline-flex shrink-0 appearance-none items-center whitespace-nowrap border-0 bg-transparent p-0 text-sm font-semibold after:absolute after:inset-x-0 after:-inset-y-3 lg:min-h-8 lg:after:content-none lg:[@media(pointer:coarse)]:min-h-11',
                tone === 'spotify' ? 'text-[#1ed760] hover:text-[#1DB954]' : 'text-primary hover:text-primary/90'
              )}
            >
              {actionLabel}
            </button>
          </>
        ) : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss this note"
          title="Dismiss this note"
          className="-my-1 -mr-1.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md text-white/60 hover:bg-white/10 hover:text-white [@media(pointer:coarse)]:size-11"
        >
          <X aria-hidden className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Standard tool chrome: a left-aligned title, one sentence, and one status row.
 * The header shares its left edge with the site header at every width.
 */
export function ToolPage({
  title,
  description,
  hint,
  onExample,
  exampleLabel = 'Try an Example',
  exampleDisabled,
  banner,
  needsAuth,
  onAuthClick,
  isDemo,
  demoNotice,
  variant = 'default',
  maxWidth,
  contentMaxWidth,
  headerExtra,
  children,
}: ToolPageProps) {
  const isSpotify = variant === 'spotify';
  const notice = getApiNotice(window.location.pathname);
  const [noticeDismissed, setNoticeDismissed] = useState(() => Boolean(notice && readDismissed(notice.id)));
  const visibleNotice = noticeDismissed ? undefined : notice;
  const statusText = isDemo
    ? (visibleNotice?.body ?? demoNotice ?? 'This is a read-only preview. Connect Spotify to use your own library.')
    : needsAuth
      ? (visibleNotice?.body ?? 'Connect Spotify to use this tool.')
      : visibleNotice?.body;
  const showConnect = Boolean((isDemo || needsAuth) && onAuthClick);

  return (
    <div className="mx-auto flex w-full max-w-screen-xl flex-col px-4 py-5 text-foreground sm:px-6 lg:py-7">
      <header className="mb-5 flex flex-col gap-3 lg:mb-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between lg:gap-8">
          <div className="min-w-0 max-w-[70ch]">
            <h1 className="font-display text-balance text-[1.625rem] font-bold leading-tight tracking-tight sm:text-[2rem]">
              {title}
            </h1>
            {description ? (
              <p className="mt-1.5 text-pretty text-[0.95rem] leading-relaxed text-muted-foreground sm:text-base">
                {description}
              </p>
            ) : null}
          </div>
          {statusText ? (
            <ToolStatus
              tone={isSpotify ? 'spotify' : 'warning'}
              actionLabel={showConnect ? 'Connect Spotify' : undefined}
              onAction={onAuthClick}
              onDismiss={
                visibleNotice
                  ? () => {
                      writeDismissed(visibleNotice.id);
                      setNoticeDismissed(true);
                    }
                  : undefined
              }
              className="lg:max-w-[520px] lg:shrink-0"
            >
              {statusText}
            </ToolStatus>
          ) : null}
        </div>
        {banner}
        {hint || onExample ? (
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {hint ? <span className="text-pretty">{hint}</span> : null}
            {onExample ? (
              <button
                type="button"
                onClick={onExample}
                disabled={exampleDisabled}
                className={cn(
                  'inline-flex min-h-8 appearance-none items-center border-0 bg-transparent p-0 font-medium underline underline-offset-[3px] disabled:opacity-50 [@media(pointer:coarse)]:min-h-11',
                  isSpotify ? 'text-[#1DB954] hover:text-[#1ed760]' : 'text-primary hover:text-primary/90'
                )}
              >
                {exampleLabel}
              </button>
            ) : null}
          </p>
        ) : null}
        {headerExtra ? <div className="flex flex-wrap gap-2">{headerExtra}</div> : null}
      </header>

      <div
        className="flex w-full flex-col items-stretch"
        style={{ maxWidth: toCssWidth(contentMaxWidth ?? maxWidth) ?? '100%' }}
      >
        <ToolExampleContext.Provider value={{ onExample, label: exampleLabel, disabled: exampleDisabled }}>
          {children}
        </ToolExampleContext.Provider>
      </div>
    </div>
  );
}

/**
 * Form beside results at wide widths, so inputs and output share the screen.
 * The columns stack on a phone, with the results under the form.
 */
export function ToolSplit({ form, results }: { form: ReactNode; results: ReactNode }) {
  return (
    <div className="grid w-full grid-cols-1 items-start gap-6 lg:grid-cols-[440px_minmax(0,1fr)] lg:gap-7">
      <div className="flex min-w-0 flex-col gap-4">{form}</div>
      <div className="min-w-0">{results}</div>
    </div>
  );
}

/** What the results column shows before the first run. */
export function ToolResultsPlaceholder({ children }: { children: ReactNode }) {
  const example = useContext(ToolExampleContext);
  return (
    <div className="hidden min-h-[320px] items-center justify-center rounded-2xl bg-white/[0.03] p-8 text-center text-[0.95rem] text-muted-foreground shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07)] lg:flex">
      <div className="flex flex-col items-center gap-4">
        <p className="max-w-[46ch] text-pretty">{children}</p>
        {example.onExample ? (
          <button
            type="button"
            onClick={example.onExample}
            disabled={example.disabled}
            className="inline-flex min-h-11 items-center rounded-full bg-white/10 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/15 disabled:opacity-50"
          >
            {example.label}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default ToolPage;
